import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './_styles_/ClientApplicationsTableRow.css';
import {
    clientApplicationsType,
    clientApplicationType,
} from '../../types';
import ConfirmButton from '../buttons/ConfirmButton';
import TextField from '../fields/TextField';
import useExtendedState from '../../hooks/useExtendedState';
import useSingleValidator from '../../hooks/useSingleValidator';
import {
    settingsApplicationDescriptionValidator,
    settingsApplicationIdValidator,
} from '../../validators/settings';
import CheckboxField from '../fields/CheckboxField';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import { isTemporaryId } from '../../utils/tableRecords';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';
import { useMemo } from 'react';

ClientApplicationsTableRow.propTypes = {
    clientApplication: clientApplicationType.isRequired,
    clientApplications: clientApplicationsType.isRequired,
    onClientApplicationDelete: PropTypes.func.isRequired,
    onClientApplicationValidated: PropTypes.func.isRequired,
    backendChanged: PropTypes.bool.isRequired,
    doReset: PropTypes.bool.isRequired,
};

function ClientApplicationsTableRow(props) {
    const {
        clientApplication,
        clientApplications,
        onClientApplicationDelete,
        onClientApplicationValidated,
        backendChanged,
        doReset,
    } = props;
    const { t } = useTranslation();
    const applicationIdState = useExtendedState(clientApplication.applicationId);
    const activeState = useExtendedState(clientApplication.active);
    const descriptionState = useExtendedState(clientApplication.description);
    const {
        validator: applicationIdValidator,
        isValid: isApplicationIdValid,
        reset: resetApplicationIdValidator,
    } = useSingleValidator(true, useMemo(() => compositeValidator(settingsApplicationIdValidator(t, clientApplications), wafValidator(t)), [t, clientApplications]));
    const {
        validator: descriptionValidator,
        isValid: isDescriptionValid,
        reset: resetDescriptionValidator,
    } = useSingleValidator(true, useMemo(() => compositeValidator(settingsApplicationDescriptionValidator(t), wafValidator(t)), [t]));
    const onApplicationIdValidated = (newApplicationId, isValid) => {
        const data = {
            applicationId: newApplicationId,
            description: descriptionState.value,
            active: activeState.value,
        };
        isDescriptionValid()
            .then(() => onClientApplicationValidated(data, isValid))
            .catch(() => onClientApplicationValidated(data, false));
    };
    const onDescriptionValidated = (newDescription, isValid) => {
        const data = {
            applicationId: applicationIdState.value,
            description: newDescription,
            active: activeState.value,
        };
        isApplicationIdValid()
            .then(() => onClientApplicationValidated(data, isValid))
            .catch(() => onClientApplicationValidated(data, false));
    };
    const onChangeActive = (newActive) => {
        const data = {
            applicationId: applicationIdState.value,
            description: descriptionState.value,
            active: newActive,
        };
        Promise.all([isApplicationIdValid(), isDescriptionValid()])
            .then(() => onClientApplicationValidated(data, true))
            .catch(() => onClientApplicationValidated(data, false));
    };
    const deleteClientApplication = () => {
        onClientApplicationDelete(clientApplication.id);
    };
    useEffectOnUpdate(() => {
        if (doReset) {
            resetApplicationIdValidator();
            resetDescriptionValidator();
            applicationIdState.reset();
            descriptionState.reset();
            activeState.reset();
        }
    }, [doReset]);
    useEffectOnUpdate(() => {
        if (backendChanged) {
            applicationIdState.changeInitialState(applicationIdState.value);
            applicationIdState.reset();
            descriptionState.changeInitialState(descriptionState.value);
            descriptionState.reset();
            activeState.changeInitialState(activeState.value);
            activeState.reset();
        }
    }, [backendChanged, applicationIdState.value, descriptionState.value, activeState.value]);
    return (
        <div className="detail-list-row client-application-table-row">
            <div className="client-application-cell--small client-applications-table-row--remove">
                <ConfirmButton
                    type="button"
                    className="delete-button button-small button-bordered"
                    onConfirm={deleteClientApplication}
                    message={t('settings.client-applications.confirm-delete-client-application', { applicationId: clientApplication.applicationId })}
                >
                    <FontAwesomeIcon icon="times" />
                </ConfirmButton>
            </div>
            <div className="client-application-cell client-applications-table-row--id">{isTemporaryId(clientApplication.id) ? '' : clientApplication.id}</div>
            <div className="client-application-cell client-applications-table-row--application-id">
                <TextField
                    state={applicationIdState}
                    className="client-applications-table-row--application-id--value table-input"
                    inputId="client-applications-table-row--application-id"
                    onValidated={onApplicationIdValidated}
                    validator={applicationIdValidator}
                    useDynamicWidth={false}
                />
            </div>
            <div className="client-application-cell client-applications-table-row--description">
                <TextField
                    state={descriptionState}
                    className="client-applications-table-row--description--value table-input"
                    inputId="client-applications-table-row--description"
                    onValidated={onDescriptionValidated}
                    validator={descriptionValidator}
                    useDynamicWidth={false}
                />
            </div>
            <div className="client-application-cell client-applications-table-row--active">
                <CheckboxField
                    state={activeState}
                    className="client-applications-table-row--active--value"
                    inputId="client-applications-table-row--active"
                    onChange={onChangeActive}
                />
            </div>
        </div>
    );
}

export default ClientApplicationsTableRow;
